import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useProjectStore from '../store/projectStore';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';
import { getEmployees, getDepartments } from '../services/employeeService';
import { createStage } from '../services/stageService';

const todayStr = () => new Date().toISOString().slice(0, 10);

const initialForm = {
  name: '',
  client_name: '',
  location: '',
  department_id: '',
  project_hod_id: '',
  parent_project_id: '',
  budget_hours: '',
  budgeted_cost: '',
  budgeted_material_cost: '',
  budgeted_site_cost: '',
  weight_kg: '',
  manufacturing_start_date: todayStr(),
  manufacturing_expected_completion_date: '',
  site_installation_start_date: '',
  site_installation_expected_completion_date: '',
};

const CreateProjectPage = () => {
  const [form, setForm] = useState(initialForm);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [queuedStages, setQueuedStages] = useState([]);
  const [newStageName, setNewStageName] = useState('');
  const [newStageRequiresInspection, setNewStageRequiresInspection] = useState(false);
  const { createProject, projects, fetchProjects, isLoading } = useProjectStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    getDepartments().then((res) => setDepartments(res.data.departments || [])).catch(() => {});
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only top-level projects can be picked as a parent - a sub-project can't
  // itself become a parent, keeping the hierarchy one level deep.
  const eligibleParents = projects.filter((p) => !p.parent_project_id);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const addQueuedStage = () => {
    if (!newStageName.trim()) return;
    setQueuedStages((prev) => [...prev, { name: newStageName, requires_inspection: newStageRequiresInspection }]);
    setNewStageName('');
    setNewStageRequiresInspection(false);
  };

  const removeQueuedStage = (idx) => setQueuedStages((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.manufacturing_expected_completion_date || !form.site_installation_expected_completion_date) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    const payload = {
      name: form.name,
      client_name: form.client_name || null,
      location: form.location || null,
      department_id: form.department_id ? Number(form.department_id) : null,
      project_hod_id: form.project_hod_id ? Number(form.project_hod_id) : null,
      parent_project_id: form.parent_project_id ? Number(form.parent_project_id) : null,
      created_by_id: user?.id,
      budget_hours: form.budget_hours ? Number(form.budget_hours) : null,
      budgeted_cost: form.budgeted_cost ? Number(form.budgeted_cost) : null,
      budgeted_material_cost: form.budgeted_material_cost ? Number(form.budgeted_material_cost) : null,
      budgeted_site_cost: form.budgeted_site_cost ? Number(form.budgeted_site_cost) : null,
      weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
      manufacturing_start_date: form.manufacturing_start_date || null,
      manufacturing_expected_completion_date: form.manufacturing_expected_completion_date,
      site_installation_start_date: form.site_installation_start_date || null,
      site_installation_expected_completion_date: form.site_installation_expected_completion_date,
    };

    const result = await createProject(payload);
    if (result.success) {
      const projectId = result.data.project_id;
      for (const stage of queuedStages) {
        try {
          await createStage({ project_id: projectId, name: stage.name, requires_inspection: stage.requires_inspection });
        } catch (err) {
          showToast(`Project created, but failed to add stage "${stage.name}"`, 'error');
        }
      }
      showToast('Project created', 'success');
      navigate(`/projects/${projectId}`);
    } else {
      showToast(result.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto glass-card p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-6">New Project</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input
              required
              value={form.name}
              onChange={handleChange('name')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
              <input
                value={form.client_name}
                onChange={handleChange('client_name')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                value={form.location}
                onChange={handleChange('location')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Part of a larger project (optional)</label>
            <select
              value={form.parent_project_id}
              onChange={handleChange('parent_project_id')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Not part of another project</option>
              {eligibleParents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              E.g. this is one of several tanks under a bigger client engagement like "Erfan Darou" -
              pick that project here. Deadlines/punch points/daily entries still stay separate per tank;
              this just lets the parent show combined costing across all of them.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={form.department_id}
                onChange={handleChange('department_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Unassigned</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project HOD</label>
              <select
                value={form.project_hod_id}
                onChange={handleChange('project_hod_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Unassigned</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.role}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Hours</label>
              <input
                type="number"
                value={form.budget_hours}
                onChange={handleChange('budget_hours')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budgeted Cost (₹)</label>
              <input
                type="number"
                value={form.budgeted_cost}
                onChange={handleChange('budgeted_cost')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
              <input
                type="number"
                value={form.weight_kg}
                onChange={handleChange('weight_kg')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="For production tracking"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budgeted Material Cost (₹)</label>
              <input
                type="number"
                value={form.budgeted_material_cost}
                onChange={handleChange('budgeted_material_cost')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="For the Cash Flow module"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budgeted Site Install Cost (₹)</label>
              <input
                type="number"
                value={form.budgeted_site_cost}
                onChange={handleChange('budgeted_site_cost')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="For the Cash Flow module"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">Manufacturing Phase</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={form.manufacturing_start_date}
                  onChange={handleChange('manufacturing_start_date')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Completion *</label>
                <input
                  required
                  type="date"
                  value={form.manufacturing_expected_completion_date}
                  onChange={handleChange('manufacturing_expected_completion_date')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">Site Installation Phase</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={form.site_installation_start_date}
                  onChange={handleChange('site_installation_start_date')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Completion *</label>
                <input
                  required
                  type="date"
                  value={form.site_installation_expected_completion_date}
                  onChange={handleChange('site_installation_expected_completion_date')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">Production Plan (optional)</div>
            <p className="text-xs text-gray-500 mb-2">
              Add milestones now, or skip this and manage them later from the project page.
            </p>
            {queuedStages.length > 0 && (
              <ul className="mb-2 space-y-1">
                {queuedStages.map((s, idx) => (
                  <li key={idx} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-1.5">
                    <span>
                      #{idx + 1} {s.name} {s.requires_inspection && <span className="text-xs text-komal-blue ml-1">(requires inspection)</span>}
                    </span>
                    <button type="button" onClick={() => removeQueuedStage(idx)} className="text-red-500 hover:text-red-700 text-xs">
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2 items-center flex-wrap">
              <input
                placeholder="Stage name (e.g. Dishing)"
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <label className="flex items-center gap-1 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={newStageRequiresInspection}
                  onChange={(e) => setNewStageRequiresInspection(e.target.checked)}
                />
                Requires inspection
              </label>
              <button
                type="button"
                onClick={addQueuedStage}
                className="px-3 py-2 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                + Add Stage
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {isLoading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProjectPage;
